import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Grid,
  FormControl,
  FormHelperText,
  Alert,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Autocomplete,
  Avatar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AccountBalance as FundingIcon,
  Info as InfoIcon,
  AddCircleOutline as NewIcon,
  Group as GroupIcon,
  Article as PublicationIcon,
  Science as ResearchIcon,
  School as TeachingIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

// Collaboration type configurations
const COLLABORATION_TYPES = {
  funding: {
    id: 'funding',
    label: 'Funding Call/Grant',
    description: 'Traditional consortium for a funding call application',
    icon: FundingIcon,
    color: '#1976d2',
    requiresCall: true,
  },
  publication: {
    id: 'publication',
    label: 'Scientific Publication',
    description: 'Consortium for co-authoring research papers, books, or articles',
    icon: PublicationIcon,
    color: '#2e7d32',
    fields: ['publicationType', 'targetJournal', 'expectedDate'],
  },
  // teaching: {
  //   id: 'teaching',
  //   label: 'Teaching/Course',
  //   description: 'Course development, degree programs, or training workshops',
  //   icon: TeachingIcon,
  //   color: '#0288d1',
  //   fields: ['teachingType', 'targetAudience', 'credits'],
  // },
  // research: {
  //   id: 'research',
  //   label: 'Research Project',
  //   description: 'Collaborative research project or study',
  //   icon: ResearchIcon,
  //   color: '#ed6c02',
  //   fields: ['researchArea', 'methodology', 'fundingSource'],
  // },
  // innovation: {
  //   id: 'innovation',
  //   label: 'Innovation/Product Development',
  //   description: 'Developing software, hardware, patents, or prototypes',
  //   icon: InnovationIcon,
  //   color: '#9c27b0',
  //   fields: ['innovationType', 'productName', 'trl'],
  // },
  // outreach: {
  //   id: 'outreach',
  //   label: 'Outreach Activities',
  //   description: 'Community engagement, science communication, or policy work',
  //   icon: OutreachIcon,
  //   color: '#d32f2f',
  //   fields: ['outreachType', 'targetAudience', 'geographicScope'],
  // },
  // distribution: {
  //   id: 'distribution',
  //   label: 'Product Distribution',
  //   description: 'Supply chain, logistics, or distribution network',
  //   icon: DistributionIcon,
  //   color: '#5e35b1',
  //   fields: ['distributionType', 'distributionModel', 'coverage'],
  // },
};

// Publication types - map display name to backend value
const PUBLICATION_TYPES = [
  { label: 'Journal Article', value: 'journal_article' },
  { label: 'Book/Monograph', value: 'book' },
  { label: 'Conference Paper', value: 'conference_paper' },
  { label: 'Book Chapter', value: 'book_chapter' },
  { label: 'Technical Report', value: 'technical_report' },
  { label: 'White Paper', value: 'white_paper' },
  { label: 'Preprint', value: 'preprint' },
  { label: 'Thesis/Dissertation', value: 'thesis' },
];


// Innovation types - map display name to backend value
const INNOVATION_TYPES = [
  { label: 'Software/Application', value: 'software' },
  { label: 'Hardware/Device', value: 'hardware' },
  { label: 'Patent', value: 'patent' },
  { label: 'Prototype', value: 'prototype' },
  { label: 'Platform/Service', value: 'platform' },
  { label: 'Algorithm/Method', value: 'algorithm' },
];


// Teaching types - map display name to backend value
const TEACHING_TYPES = [
  { label: 'Course', value: 'course' },
  { label: 'Degree Program', value: 'degree_program' },
  { label: 'Training Workshop', value: 'training' },
  { label: 'MOOC', value: 'mooc' },
  { label: 'Curriculum Development', value: 'curriculum' },
  { label: 'Bootcamp', value: 'bootcamp' },
  { label: 'Seminar Series', value: 'seminar' },
];


// Outreach types - map display name to backend value
const OUTREACH_TYPES = [
  { label: 'Community Engagement', value: 'community' },
  { label: 'Awareness Campaign', value: 'awareness' },
  { label: 'Science Communication', value: 'science_comm' },
  { label: 'Policy Engagement', value: 'policy' },
  { label: 'Citizen Science', value: 'citizen_science' },
  { label: 'Exhibition/Event', value: 'exhibition' },
  { label: 'Media Outreach', value: 'media' },
];


// Distribution types - map display name to backend value
const DISTRIBUTION_TYPES = [
  { label: 'Supply Chain', value: 'supply_chain' },
  { label: 'Logistics Network', value: 'logistics' },
  { label: 'Retail Distribution', value: 'retail' },
  { label: 'Wholesale Distribution', value: 'wholesale' },
  { label: 'Distribution Network', value: 'network' },
  { label: 'Franchise System', value: 'franchise' },
];


const EnhancedCreateConsortiumDialog = ({ 
  open, 
  onClose, 
  onCreate, 
  availableCalls = [] 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [collaborationType, setCollaborationType] = useState('');
  // Publication consortia are not tied to a funding call or a project duration
  const isPublication = collaborationType === 'publication';
  const [formData, setFormData] = useState({
    // Consortium info (one-to-one with collaboration)
    consortiumName: '',
    isPublic: true,          // visible to everyone on Otable; false = only its members see it
    duration_months: 36,
    status: 'forming',
    
    // Legacy funding call (optional)
    call: '',
    
    // Collaboration info (includes abstract which serves as consortium summary)
    collaborationTitle: '',
    abstract: '', // This serves as both collaboration abstract AND consortium summary
    collaborationStatus: 'planning',
    startDate: '',
    endDate: '',
    keywords: [],
    
    // Type-specific fields
    publicationType: '',
    targetJournal: '',
    expectedDate: '',
    
    researchArea: '',
    methodology: '',
    fundingSource: '',
    
    innovationType: '',
    productName: '',
    trl: 3,
    
    teachingType: '',
    targetAudience: '',
    credits: '',
    
    outreachType: '',
    geographicScope: '',
    
    distributionType: '',
    distributionModel: '',
    coverage: '',
    
    // Funding-specific fields
    requestedAmount: '',
    currency: 'EUR',
    researchType: 'project',
    proposalReference: '',
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState({});
  const [invitedMembers, setInvitedMembers] = useState([]);
  const [emailInvitees, setEmailInvitees] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const membersLoadedRef = useRef(false);

  // Existing consortium mode
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [selectedExistingConsortium, setSelectedExistingConsortium] = useState(null);
  const [existingConsortia, setExistingConsortia] = useState([]);
  const [loadingConsortia, setLoadingConsortia] = useState(false);
  const [consortiaQuery, setConsortiaQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Funding call search
  const [selectedCall, setSelectedCall] = useState(null);
  const [callOptions, setCallOptions] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [callSearchQuery, setCallSearchQuery] = useState('');

  const loadAllUsers = useCallback(async () => {
    if (membersLoadedRef.current) return; // already loaded this session
    membersLoadedRef.current = true;
    setMemberSearchLoading(true);
    try {
      const res = await axiosService.get('/users/', { params: { page_size: 500 } });
      setMemberSearchResults(res.data?.results || res.data || []);
    } catch {
      membersLoadedRef.current = false; // allow retry on error
      setMemberSearchResults([]);
    } finally {
      setMemberSearchLoading(false);
    }
  }, []);

  // Load users once when the invite step becomes active
  useEffect(() => {
    if (activeStep === 3) {
      loadAllUsers();
    }
  }, [activeStep, loadAllUsers]);

  const steps = ['Select Type', 'Consortium & Project Info', 'Type-Specific Details', 'Invite Members', 'Review & Create'];

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setActiveStep(0);
      setCollaborationType('');
      setFormData({
        consortiumName: '',
        isPublic: true,
        duration_months: 36,
        status: 'forming',
        call: '',
        collaborationTitle: '',
        abstract: '',
        collaborationStatus: 'planning',
        startDate: '',
        endDate: '',
        keywords: [],
        publicationType: '',
        targetJournal: '',
        expectedDate: '',
        researchArea: '',
        methodology: '',
        fundingSource: '',
        innovationType: '',
        productName: '',
        trl: 3,
        teachingType: '',
        targetAudience: '',
        credits: '',
        outreachType: '',
        geographicScope: '',
        distributionType: '',
        distributionModel: '',
        coverage: '',
        requestedAmount: '',
        currency: 'EUR',
        researchType: 'project',
        proposalReference: '',
      });
      setErrors({});
      setInvitedMembers([]);
      setMemberSearchResults([]);
      setMemberQuery('');
      membersLoadedRef.current = false;
      setMode('new');
      setSelectedExistingConsortium(null);
      setExistingConsortia([]);
      setConsortiaQuery('');
      setSubmitting(false);
      setSelectedCall(null);
      setCallOptions([]);
      setCallSearchQuery('');
    }
  }, [open]);

  // Load initial funding call list when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoadingCalls(true);
    axiosService.get('/funding/calls/', { params: { page_size: 30 } })
      .then(res => setCallOptions(res?.data?.results || res?.data || []))
      .catch(() => setCallOptions([]))
      .finally(() => setLoadingCalls(false));
  }, [open]);

  // Debounced funding call search
  useEffect(() => {
    if (callSearchQuery.length < 2) {
      if (callSearchQuery === '') {
        setLoadingCalls(true);
        axiosService.get('/funding/calls/', { params: { page_size: 30 } })
          .then(res => setCallOptions(res?.data?.results || res?.data || []))
          .catch(() => setCallOptions([]))
          .finally(() => setLoadingCalls(false));
      }
      return;
    }
    const timer = setTimeout(() => {
      setLoadingCalls(true);
      axiosService.get('/funding/calls/', { params: { search: callSearchQuery, page_size: 20 } })
        .then(res => setCallOptions(res?.data?.results || res?.data || []))
        .catch(() => setCallOptions([]))
        .finally(() => setLoadingCalls(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [callSearchQuery]);

  // Load initial consortia list when mode switches to 'existing'
  useEffect(() => {
    if (mode !== 'existing') return;
    setLoadingConsortia(true);
    axiosService.get('/consortium/consortiums/', { params: { page_size: 30 } })
      .then(res => setExistingConsortia(res.data?.results || res.data || []))
      .catch(() => setExistingConsortia([]))
      .finally(() => setLoadingConsortia(false));
  }, [mode]);

  // Debounced consortia search
  useEffect(() => {
    if (mode !== 'existing') return;
    if (consortiaQuery.length < 2) {
      if (consortiaQuery === '') {
        setLoadingConsortia(true);
        axiosService.get('/consortium/consortiums/', { params: { page_size: 30 } })
          .then(res => setExistingConsortia(res.data?.results || res.data || []))
          .catch(() => setExistingConsortia([]))
          .finally(() => setLoadingConsortia(false));
      }
      return;
    }
    const timer = setTimeout(() => {
      setLoadingConsortia(true);
      axiosService.get('/consortium/consortiums/', { params: { search: consortiaQuery, page_size: 20 } })
        .then(res => setExistingConsortia(res.data?.results || res.data || []))
        .catch(() => setExistingConsortia([]))
        .finally(() => setLoadingConsortia(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [consortiaQuery, mode]);

  // Auto-fill Consortium Title when a call is selected and title is still empty
  useEffect(() => {
    if (selectedCall?.title && !formData.collaborationTitle) {
      setFormData(prev => ({ ...prev, collaborationTitle: `Consortium for ${selectedCall.title}` }));
    }
  }, [selectedCall]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Select Type
        if (!collaborationType) {
          newErrors.collaborationType = 'Please select a collaboration type';
        }
        if (mode === 'existing' && !selectedExistingConsortium) {
          newErrors.existingConsortium = 'Please select an existing consortium';
        }
        break;

      case 1: // Basic Info
        if (mode === 'new' && (!formData.consortiumName || formData.consortiumName.trim().length < 3)) {
          newErrors.consortiumName = 'Consortium name must be at least 3 characters';
        }
        if (!formData.collaborationTitle || formData.collaborationTitle.trim().length < 3) {
          newErrors.collaborationTitle = 'Consortium title must be at least 3 characters';
        }
        if (!formData.abstract || formData.abstract.trim().length < 20) {
          newErrors.abstract = 'Abstract/Summary must be at least 20 characters';
        }
        if (collaborationType === 'funding' && !formData.call) {
          newErrors.call = 'Please select a funding call';
        }
        break;
        
      case 2: // Collaboration Details (type-specific fields and dates)
        // Title and abstract already validated in step 1
        
        // Validate type-specific fields
        if (collaborationType === 'publication' && !formData.publicationType) {
          newErrors.publicationType = 'Publication type is required';
        }
        if (collaborationType === 'innovation' && !formData.innovationType) {
          newErrors.innovationType = 'Innovation type is required';
        }
        if (collaborationType === 'teaching' && !formData.teachingType) {
          newErrors.teachingType = 'Teaching type is required';
        }
        if (collaborationType === 'outreach' && !formData.outreachType) {
          newErrors.outreachType = 'Outreach type is required';
        }
        if (collaborationType === 'distribution' && !formData.distributionType) {
          newErrors.distributionType = 'Distribution type is required';
        }
        if (collaborationType === 'research' && !formData.researchArea) {
          newErrors.researchArea = 'Research area is required';
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !formData.keywords.includes(kw) && formData.keywords.length < 5) {
      setFormData(prev => ({ ...prev, keywords: [...prev.keywords, kw] }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const buildTypeSpecificData = () => {
    const d = {};
    switch (collaborationType) {
      case 'publication':
        d.publication_type = formData.publicationType;
        d.journal_name = formData.targetJournal;
        d.publication_date = formData.expectedDate;
        break;
      case 'research':
        d.research_type = formData.researchType || 'project';
        d.research_area = formData.researchArea;
        d.methodology = formData.methodology;
        d.funding_amount = formData.fundingSource;
        break;
      case 'innovation':
        d.innovation_type = formData.innovationType;
        d.product_name = formData.productName;
        d.technology_readiness_level = formData.trl;
        break;
      case 'teaching':
        d.teaching_type = formData.teachingType;
        d.target_audience = formData.targetAudience;
        d.credits = formData.credits;
        break;
      case 'outreach':
        d.outreach_type = formData.outreachType;
        d.target_audience = formData.targetAudience;
        d.geographic_scope = formData.geographicScope;
        break;
      case 'distribution':
        d.distribution_type = formData.distributionType;
        d.distribution_model = formData.distributionModel;
        d.geographic_coverage = formData.coverage;
        break;
      case 'funding':
        if (formData.requestedAmount) d.requested_amount = formData.requestedAmount;
        if (formData.currency) d.currency = formData.currency;
        if (formData.proposalReference) d.proposal_reference = formData.proposalReference;
        break;
      default:
        break;
    }
    // Drop empty values so optional fields don't fail backend validation
    return Object.fromEntries(
      Object.entries(d).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    if (mode === 'existing') {
      setSubmitting(true);
      try {
        const collaborationData = {
          consortium: selectedExistingConsortium.id,
          title: formData.collaborationTitle,
          category: collaborationType,
          abstract: formData.abstract,
          status: formData.collaborationStatus,
          start_date: isPublication ? null : (formData.startDate || null),
          end_date: formData.endDate || null,
          keywords: formData.keywords,
          ...(formData.call && !isPublication ? { call: formData.call } : {}),
          ...buildTypeSpecificData(),
        };

        await axiosService.post('/consortium/collaborations/', collaborationData);
        // Backend signal automatically notifies existing consortium members

        onCreate({});
        onClose();
      } catch (err) {
        console.error('Failed to create collaboration for existing consortium:', err);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // New consortium mode — existing flow
    const collaborationData = {
      consortium_name: formData.consortiumName,
      consortium_is_public: formData.isPublic,
      title: formData.collaborationTitle,
      category: collaborationType,
      abstract: formData.abstract,
      status: formData.collaborationStatus,
      start_date: isPublication ? null : (formData.startDate || null),
      end_date: formData.endDate || null,
      keywords: formData.keywords,
      ...(formData.call && !isPublication ? { call: formData.call } : {}),
    };

    onCreate({
      collaboration: collaborationData,
      typeSpecific: buildTypeSpecificData(),
      collaborationType,
      invitedMembers: invitedMembers.map(u => u.id),
      emailInvitees,
      ...(isPublication ? {} : { duration_months: formData.duration_months }),
      status: formData.status,
    });

    onClose();
  };

  const renderInviteMembers = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>Invite Members (Optional)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search for users by name to invite them to the consortium. You can also invite members after creation.
      </Typography>
      <Autocomplete
        multiple
        options={(() => {
          const q = memberQuery.trim().toLowerCase();
          if (!q) return memberSearchResults;
          return memberSearchResults.filter((u) => {
            const full = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
            return (
              full.includes(q) ||
              (u.username || '').toLowerCase().includes(q) ||
              (u.email || '').toLowerCase().includes(q)
            );
          });
        })()}
        value={invitedMembers}
        getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email || ''}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        onInputChange={(_, val) => setMemberQuery(val)}
        onChange={(_, val) => setInvitedMembers(val)}
        loading={memberSearchLoading}
        filterOptions={(x) => x}
        slotProps={{ popper: { style: { zIndex: 10001 } } }}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={option.avatar} sx={{ width: 28, height: 28, fontSize: 12 }}>
              {(option.first_name?.[0] || option.username?.[0] || '?').toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2">{`${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username}</Typography>
              {option.position && <Typography variant="caption" color="text.secondary">{option.position}</Typography>}
            </Box>
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              avatar={<Avatar src={option.avatar} sx={{ width: 22, height: 22, fontSize: 10 }}>{(option.first_name?.[0] || '?').toUpperCase()}</Avatar>}
              label={`${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username}
              {...getTagProps({ index })}
              key={option.id}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search users by name"
            placeholder="Type to filter by name, username or email..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {memberSearchLoading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {/* Email invitations for non-registered users */}
      <Divider sx={{ my: 2 }}>
        <Typography variant="caption" color="text.secondary">or invite by email</Typography>
      </Divider>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          type="email"
          label="Email address"
          placeholder="colleague@university.edu"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addEmailInvitee();
            }
          }}
          helperText="They will receive an email with a registration link"
        />
        <Button
          variant="outlined"
          size="small"
          sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start', mt: 0.25 }}
          onClick={addEmailInvitee}
        >
          Add
        </Button>
      </Box>
      {emailInvitees.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {emailInvitees.map((email) => (
            <Chip
              key={email}
              label={email}
              size="small"
              onDelete={() => setEmailInvitees(prev => prev.filter(e => e !== email))}
            />
          ))}
        </Box>
      )}
      {(invitedMembers.length > 0 || emailInvitees.length > 0) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {invitedMembers.length > 0 && `${invitedMembers.length} registered user${invitedMembers.length > 1 ? 's' : ''}`}
          {invitedMembers.length > 0 && emailInvitees.length > 0 && ' and '}
          {emailInvitees.length > 0 && `${emailInvitees.length} email address${emailInvitees.length > 1 ? 'es' : ''}`}
          {' will be invited after the consortium is created.'}
        </Alert>
      )}
    </Box>
  );

  const addEmailInvitee = () => {
    const email = emailInput.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) return;
    if (emailInvitees.includes(email)) return;
    // Warn if the email matches a registered member already selected
    if (invitedMembers.some(u => (u.email || '').toLowerCase() === email)) return;
    setEmailInvitees(prev => [...prev, email]);
    setEmailInput('');
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderTypeSelection();
      case 1:
        return renderBasicInfo();
      case 2:
        return renderCollaborationDetails();
      case 3:
        return renderInviteMembers();
      case 4:
        return renderReview();
      default:
        return null;
    }
  };

  const renderTypeSelection = () => (
    <Box sx={{ py: 2 }}>
      {/* Consortium mode toggle */}
      <Typography variant="subtitle2" gutterBottom>
        Are you starting fresh or adding a project to an existing consortium?
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card
          sx={{
            cursor: 'pointer',
            border: 2,
            borderColor: mode === 'new' ? 'primary.main' : 'divider',
            bgcolor: mode === 'new' ? 'primary.50' : 'background.paper',
            flex: 1,
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'primary.main' },
          }}
          onClick={() => { setMode('new'); setSelectedExistingConsortium(null); setErrors({}); }}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NewIcon sx={{ color: mode === 'new' ? 'primary.main' : 'text.secondary' }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>Create New Consortium</Typography>
                <Typography variant="caption" color="text.secondary">Start a brand new consortium and project</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card
          sx={{
            cursor: 'pointer',
            border: 2,
            borderColor: mode === 'existing' ? 'secondary.main' : 'divider',
            bgcolor: mode === 'existing' ? 'secondary.50' : 'background.paper',
            flex: 1,
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'secondary.main' },
          }}
          onClick={() => { setMode('existing'); setErrors({}); }}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon sx={{ color: mode === 'existing' ? 'secondary.main' : 'text.secondary' }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>I Already Have a Consortium</Typography>
                <Typography variant="caption" color="text.secondary">Add a new project to an existing consortium</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Existing consortium search (shown when mode='existing') */}
      {mode === 'existing' && (
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            value={selectedExistingConsortium}
            onChange={(_, val) => { setSelectedExistingConsortium(val); setErrors(prev => ({ ...prev, existingConsortium: undefined })); }}
            onInputChange={(_, val, reason) => {
              if (reason === 'input') setConsortiaQuery(val);
              else if (reason === 'clear') setConsortiaQuery('');
            }}
            options={existingConsortia}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            filterOptions={(x) => x}
            loading={loadingConsortia}
            slotProps={{ popper: { style: { zIndex: 10001 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Your Consortium"
                placeholder="Search by name…"
                error={!!errors.existingConsortium}
                helperText={errors.existingConsortium || 'Search for one of your existing consortia'}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingConsortia && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                  {option.summary && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                      {option.summary}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          />
          {selectedExistingConsortium && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Existing members of <strong>{selectedExistingConsortium.name}</strong> will be notified about the new project.
            </Alert>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary">Select Project Type</Typography>
      </Divider>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the primary focus of your project. This determines which fields are required.
      </Typography>

      <Grid container spacing={2}>
        {Object.values(COLLABORATION_TYPES).map((type) => {
          const Icon = type.icon;
          const isSelected = collaborationType === type.id;

          return (
            <Grid item xs={12} sm={6} key={type.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? type.color : 'divider',
                  bgcolor: isSelected ? `${type.color}08` : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: type.color,
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => {
                  setCollaborationType(type.id);
                  setErrors({});
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Icon sx={{ color: type.color, fontSize: 32, mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {type.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {type.description}
                  </Typography>
                  {type.requiresCall && (
                    <Chip label="Requires Call" size="small" color="primary" sx={{ mt: 1 }} />
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {errors.collaborationType && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.collaborationType}
        </Alert>
      )}
    </Box>
  );

  const renderBasicInfo = () => {
    const selectedType = COLLABORATION_TYPES[collaborationType];
    
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {selectedType && (
            <>
              {React.createElement(selectedType.icon, { sx: { color: selectedType.color, mr: 1 } })}
              <Typography variant="h6">
                {selectedType.label} Consortium
              </Typography>
            </>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={3} sm={3}>
            {mode === 'existing' ? (
              <Box sx={{ p: 1.5, border: 1, borderColor: 'secondary.light', borderRadius: 1, bgcolor: 'secondary.50' }}>
                <Typography variant="caption" color="text.secondary">Selected Consortium</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <GroupIcon sx={{ color: 'secondary.main' }} />
                  <Typography variant="body1" fontWeight={600}>{selectedExistingConsortium?.name}</Typography>
                </Box>
              </Box>
            ) : (
              <TextField
                label="Consortium Name"
                fullWidth
                required
                value={formData.consortiumName}
                onChange={(e) => setFormData(prev => ({ ...prev, consortiumName: e.target.value }))}
                error={!!errors.consortiumName}
                helperText={errors.consortiumName || 'Short name for the partnership'}
              />
            )}
          </Grid>

          <Grid item xs={9} sm={9}>
            <TextField
              label="Keywords"
              fullWidth
              placeholder="Add keywords (up to 5), press Enter"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddKeyword(); }
              }}
              disabled={formData.keywords.length >= 5}
              helperText={formData.keywords.length >= 5 ? 'Maximum 5 keywords reached' : `${formData.keywords.length}/5 keywords`}
            />
            {formData.keywords.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                {formData.keywords.map((kw) => (
                  <Chip key={kw} label={kw} size="small" onDelete={() => handleRemoveKeyword(kw)} />
                ))}
              </Box>
            )}
          </Grid>

          {mode === 'new' && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Who can see this consortium?</Typography>
                <ToggleButtonGroup size="small" exclusive value={formData.isPublic ? 'public' : 'hidden'}
                  onChange={(_, v) => v && setFormData(prev => ({ ...prev, isPublic: v === 'public' }))}>
                  <ToggleButton value="public" sx={{ fontSize: 13, px: 1.75, textTransform: 'none' }}>Public</ToggleButton>
                  <ToggleButton value="hidden" sx={{ fontSize: 13, px: 1.75, textTransform: 'none' }}>Members only</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {formData.isPublic
                    ? 'Everyone on Otable can find it, see its card and ask to join.'
                    : 'Hidden from search and the Hub; only invited members see it. You can change this later.'}
                </Typography>
              </Box>
            </Grid>
          )}



          <Grid item xs={12}>
            <TextField
              label="Consortium Title"
              fullWidth
              required
              value={formData.collaborationTitle}
              onChange={(e) => setFormData(prev => ({ ...prev, collaborationTitle: e.target.value }))}
              error={!!errors.collaborationTitle}
              helperText={errors.collaborationTitle || 'The specific project or initiative title (e.g., "AI-Powered Diagnostic Tools")'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Abstract / Summary"
              fullWidth
              required
              multiline
              rows={4}
              value={formData.abstract}
              onChange={(e) => setFormData(prev => ({ ...prev, abstract: e.target.value }))}
              error={!!errors.abstract}
              helperText={errors.abstract || "Describe the project goals and approach (serves as both collaboration abstract and consortium summary)"}
            />
          </Grid>

          {!isPublication && (
          <Grid item xs={12}>
            <Autocomplete
              value={selectedCall}
              onChange={(_, newValue) => {
                setSelectedCall(newValue);
                setFormData(prev => ({ ...prev, call: newValue?.id || '' }));
              }}
              onInputChange={(_, newInput, reason) => {
                if (reason === 'input') setCallSearchQuery(newInput);
                else if (reason === 'clear') setCallSearchQuery('');
              }}
              options={callOptions}
              getOptionLabel={(option) => `${option?.code}: ${option?.title || ''}`}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(x) => x}
              loading={loadingCalls}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={collaborationType === 'funding' ? 'Funding Call (Required)' : 'Related Funding Call (Optional)'}
                  required={collaborationType === 'funding'}
                  placeholder="Start typing the call details..."
                  error={!!errors.call}
                  helperText={errors.call || (collaborationType === 'funding'
                    ? 'Select the funding call this consortium is applying for'
                    : 'Optionally link to a funding call'
                  )}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCalls && <CircularProgress color="inherit" size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{option?.code}: {option.title}</Typography>
                    {option.agency_name && (
                      <Typography variant="caption" color="text.secondary">{option.agency_name}</Typography>
                    )}
                  </Box>
                </li>
              )}
              slotProps={{ popper: { style: { zIndex: 10001 } } }}
            />
          </Grid>
          )}

          {!isPublication && (
          <Grid item xs={12} sm={6}>
            <TextField
              label="Project Duration (months)"
              type="number"
              fullWidth
              required
              value={formData.duration_months}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                duration_months: parseInt(e.target.value) || 36 
              }))}
              inputProps={{ min: 6, max: 120 }}
              helperText="Expected project duration"
            />
          </Grid>
          )}

          <Grid item xs={12} sm={isPublication ? 12 : 6}>
            <TextField
              select
              label="Initial Status"
              fullWidth
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              helperText="Current consortium status"
              SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
            >
              <MenuItem value="forming">Forming</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="planning">Planning</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderCollaborationDetails = () => {
    const selectedType = COLLABORATION_TYPES[collaborationType];
    
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="h6" gutterBottom>
          Collaboration Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Provide specific details about the {selectedType?.label.toLowerCase()} collaboration.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Consortium Title"
              fullWidth
              required
              value={formData.collaborationTitle}
              onChange={(e) => setFormData(prev => ({ ...prev, collaborationTitle: e.target.value }))}
              error={!!errors.collaborationTitle}
              helperText={errors.collaborationTitle || 'The specific project or initiative title'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Abstract"
              fullWidth
              required
              multiline
              rows={4}
              value={formData.abstract}
              onChange={(e) => setFormData(prev => ({ ...prev, abstract: e.target.value }))}
              error={!!errors.abstract}
              helperText={errors.abstract || 'Detailed description of the collaboration objectives (will also serve as consortium summary)'}
            />
          </Grid>

          {isPublication ? (
            // Publications only track a submission deadline (stored as the collaboration end date)
            <Grid item xs={12} sm={6}>
              <TextField
                label="Deadline"
                type="date"
                fullWidth
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Target submission deadline"
              />
            </Grid>
          ) : (
          <>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Expected start date"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Expected completion date"
            />
          </Grid>
          </>
          )}

          <Grid item xs={12}>
            <TextField
              select
              label="Collaboration Status"
              fullWidth
              value={formData.collaborationStatus}
              onChange={(e) => setFormData(prev => ({ ...prev, collaborationStatus: e.target.value }))}
              SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
            >
              <MenuItem value="planning">Planning</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="review">Under Review</MenuItem>
              <MenuItem value="published">Published/Released</MenuItem>
            </TextField>
          </Grid>

          <Divider sx={{ width: '100%', my: 2 }} />

          {/* Type-specific fields */}
          {renderTypeSpecificFields()}

        </Grid>
      </Box>
    );
  };

  const renderTypeSpecificFields = () => {
    switch (collaborationType) {
      case 'publication':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Publication Type"
                fullWidth
                required
                value={formData.publicationType}
                onChange={(e) => setFormData(prev => ({ ...prev, publicationType: e.target.value }))}
                error={!!errors.publicationType}
                helperText={errors.publicationType}
                SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
              >
                {PUBLICATION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target Journal/Publisher"
                fullWidth
                value={formData.targetJournal}
                onChange={(e) => setFormData(prev => ({ ...prev, targetJournal: e.target.value }))}
                helperText="Where do you plan to publish?"
              />
            </Grid>
          </>
        );

      case 'research':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                label="Research Area"
                fullWidth
                required
                value={formData.researchArea}
                onChange={(e) => setFormData(prev => ({ ...prev, researchArea: e.target.value }))}
                error={!!errors.researchArea}
                helperText={errors.researchArea || 'Primary research field or domain'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Methodology"
                fullWidth
                value={formData.methodology}
                onChange={(e) => setFormData(prev => ({ ...prev, methodology: e.target.value }))}
                helperText="Research methodology or approach"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Funding Amount (EUR)"
                type="number"
                fullWidth
                value={formData.fundingSource}
                onChange={(e) => setFormData(prev => ({ ...prev, fundingSource: e.target.value }))}
                inputProps={{ min: 0, step: 'any' }}
                helperText="Expected funding amount"
              />
            </Grid>
          </>
        );

      case 'innovation':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Innovation Type"
                fullWidth
                required
                value={formData.innovationType}
                onChange={(e) => setFormData(prev => ({ ...prev, innovationType: e.target.value }))}
                error={!!errors.innovationType}
                helperText={errors.innovationType}
                SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
              >
                {INNOVATION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Product/Innovation Name"
                fullWidth
                value={formData.productName}
                onChange={(e) => setFormData(prev => ({ ...prev, productName: e.target.value }))}
                helperText="Name of the product or innovation"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography gutterBottom>
                  Technology Readiness Level (TRL): {formData.trl}
                </Typography>
                <input
                  type="range"
                  min="1"
                  max="9"
                  value={formData.trl}
                  onChange={(e) => setFormData(prev => ({ ...prev, trl: parseInt(e.target.value) }))}
                  style={{ width: '100%' }}
                />
                <FormHelperText>
                  TRL 1 (Basic principles) to TRL 9 (Actual system proven)
                </FormHelperText>
              </FormControl>
            </Grid>
          </>
        );

      case 'teaching':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Teaching Type"
                fullWidth
                required
                value={formData.teachingType}
                onChange={(e) => setFormData(prev => ({ ...prev, teachingType: e.target.value }))}
                error={!!errors.teachingType}
                helperText={errors.teachingType}
                SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
              >
                {TEACHING_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target Audience"
                fullWidth
                value={formData.targetAudience}
                onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
                helperText="Who is this course/program for?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Credits/Hours"
                type="number"
                fullWidth
                value={formData.credits}
                onChange={(e) => setFormData(prev => ({ ...prev, credits: e.target.value }))}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="ECTS credits or contact hours"
              />
            </Grid>
          </>
        );

      case 'outreach':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Outreach Type"
                fullWidth
                required
                value={formData.outreachType}
                onChange={(e) => setFormData(prev => ({ ...prev, outreachType: e.target.value }))}
                error={!!errors.outreachType}
                helperText={errors.outreachType}
                SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
              >
                {OUTREACH_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target Audience"
                fullWidth
                value={formData.targetAudience}
                onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
                helperText="Who are you reaching out to?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Geographic Scope"
                fullWidth
                value={formData.geographicScope}
                onChange={(e) => setFormData(prev => ({ ...prev, geographicScope: e.target.value }))}
                helperText="Local, regional, national, or international"
              />
            </Grid>
          </>
        );

      case 'distribution':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Distribution Type"
                fullWidth
                required
                value={formData.distributionType}
                onChange={(e) => setFormData(prev => ({ ...prev, distributionType: e.target.value }))}
                error={!!errors.distributionType}
                helperText={errors.distributionType}
                SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
              >
                {DISTRIBUTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Distribution Model"
                fullWidth
                value={formData.distributionModel}
                onChange={(e) => setFormData(prev => ({ ...prev, distributionModel: e.target.value }))}
                helperText="B2B, B2C, D2C, etc."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Geographic Coverage"
                fullWidth
                value={formData.coverage}
                onChange={(e) => setFormData(prev => ({ ...prev, coverage: e.target.value }))}
                helperText="Areas or regions covered"
              />
            </Grid>
          </>
        );

      case 'funding':
        return (
          <>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                A funding call collaboration links your consortium to a specific funding opportunity.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Requested Amount"
                type="number"
                fullWidth
                value={formData.requestedAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, requestedAmount: e.target.value }))}
                helperText="Amount requested from funding call"
                InputProps={{
                  startAdornment: '€',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Currency"
                fullWidth
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                helperText="Currency for requested amount"
                SelectProps={{ MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } }}
              >
                <MenuItem value="EUR">EUR - Euro</MenuItem>
                <MenuItem value="USD">USD - US Dollar</MenuItem>
                <MenuItem value="GBP">GBP - British Pound</MenuItem>
                <MenuItem value="CHF">CHF - Swiss Franc</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Application Reference"
                fullWidth
                value={formData.proposalReference}
                onChange={(e) => setFormData(prev => ({ ...prev, proposalReference: e.target.value }))}
                helperText="Your internal reference or proposal number (optional)"
              />
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  const renderReview = () => {
    const selectedType = COLLABORATION_TYPES[collaborationType];
    const consortiumLabel = mode === 'existing' ? selectedExistingConsortium?.name : formData.consortiumName;

    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="h6" gutterBottom>
          Review Your Project
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please review all details before {mode === 'existing' ? 'adding the project' : 'creating the consortium'}.
        </Typography>

        {mode === 'existing' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This project will be added to <strong>{selectedExistingConsortium?.name}</strong>. All existing members will receive a notification.
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              {mode === 'existing' ? 'Consortium & New Project' : 'Consortium & Collaboration Overview'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Consortium</Typography>
                <Typography variant="body2" fontWeight={600}>{consortiumLabel}</Typography>
                {mode === 'existing' && (
                  <Typography variant="caption" color="text.secondary">(existing)</Typography>
                )}
              </Grid>
              {mode === 'new' && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Visibility</Typography>
                  <Typography variant="body2">
                    {formData.isPublic ? 'Public: everyone on Otable can find and view it' : 'Hidden: only its members can see it'}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Collaboration/Project Title</Typography>
                <Typography variant="body2" fontWeight={600}>{formData.collaborationTitle}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography variant="body2">{selectedType?.label}</Typography>
              </Grid>
              {mode === 'new' && !isPublication && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography variant="body2">{formData.duration_months} months</Typography>
                </Grid>
              )}
              {formData.call && !isPublication && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Funding Call</Typography>
                  <Typography variant="body2">
                    {selectedCall?.title || 'Selected'}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Abstract / Summary</Typography>
                <Typography variant="body2">{formData.abstract}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Additional Details
            </Typography>
            <Grid container spacing={2}>
              {formData.startDate && !isPublication && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography variant="body2">{formData.startDate}</Typography>
                </Grid>
              )}
              {formData.endDate && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{isPublication ? 'Deadline' : 'End Date'}</Typography>
                  <Typography variant="body2">{formData.endDate}</Typography>
                </Grid>
              )}
              {formData.keywords.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Keywords</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {formData.keywords.map((keyword, index) => (
                      <Chip key={index} label={keyword} size="small" />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            One-to-One Relationship
          </Typography>
          <Typography variant="body2">
            A new consortium will be automatically created for this collaboration. Each collaboration has its own dedicated consortium. After creation, you'll be able to invite members and manage both consortium and collaboration details.
          </Typography>
        </Alert> */}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Theme overrides MuiMenu/MuiPopover to zIndex 9999 (for autocomplete dropdowns).
      // The dialog must sit above that so the notification panel cannot overlap it and
      // cause accidental clicks on notification action buttons.
      sx={{ zIndex: 10000 }}
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Create New Collaborative Project</Typography>
          <Tooltip title="Create a new consortium and its initial collaborative project. The consortium will serve as the organizational framework for this initiative.">
            <IconButton size="small" aria-label="Information about creating a consortium">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ minHeight: 400 }}>
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={Object.keys(errors).length > 0 || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Creating…' : mode === 'existing' ? 'Add Project' : 'Create Project'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedCreateConsortiumDialog;
